<template>
  <StaticPageLayout
    v-if="!isPageReady && !hasFatalError"
    title="Loading Recommendations"
    subtitle="Preparing this recommendation page..."
  >
    <div class="loading-inline">
      <div class="loading-spinner"></div>
      <p>Loading recommendations...</p>
    </div>
  </StaticPageLayout>

  <StaticPageLayout
    v-else-if="hasFatalError"
    title="Page Not Found"
    subtitle="We couldn't load this recommendation page."
  >
    <div class="error-box">
      <p>{{ seoPageError || errorMessage || 'Unknown error' }}</p>
      <router-link to="/best" class="browse-link">Browse all recommendation pages</router-link>
    </div>
  </StaticPageLayout>

  <StaticPageLayout
    v-else
    :title="seoPage.h1"
    :subtitle="seoPage.intro || ''"
    :last-updated="formattedLastUpdated"
  >
    <div v-if="warningMessage && games.length" :class="['results-warning', warningLevel || 'info']">
      <strong>{{ fallbackApplied ? 'Fallback active:' : 'Notice:' }}</strong>
      <span>{{ warningMessage }}</span>
    </div>

    <section v-if="games.length" class="results-section">
      <div class="results-header">
        <h2>Top Picks</h2>
        <p class="results-subtitle">Ranked by relevance to this recommendation page</p>
      </div>

      <GameList :games="games" />
    </section>

    <section v-else class="empty-state">
      <h2>No games found</h2>
      <p>This page does not currently have results.</p>
    </section>

    <section v-if="relatedPages.length" class="related-pages">
      <h2>More Recommendation Pages</h2>
      <div class="related-grid">
        <router-link
          v-for="page in relatedPages"
          :key="page.slug"
          :to="`/best/${page.slug}`"
          class="related-card"
        >
          <span class="related-card__eyebrow">{{ page.eyebrow || 'Recommendation' }}</span>
          <strong class="related-card__title">{{ page.h1 }}</strong>
          <span v-if="page.intro" class="related-card__text">{{ page.intro }}</span>
        </router-link>
      </div>
    </section>
  </StaticPageLayout>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import GameList from './GameList.vue';
import StaticPageLayout from './StaticPageLayout.vue';

export default {
  name: 'BestPage',
  components: {
    GameList,
    StaticPageLayout,
  },
  data() {
    return {
      isPageReady: false,
      hasFatalError: false,
    };
  },
  computed: {
    ...mapState([
      'seoPage',
      'seoPageLoading',
      'seoPageError',
      'seoPages',
      'games',
      'isLoading',
      'errorMessage',
      'warningMessage',
      'warningLevel',
      'fallbackApplied',
    ]),
    formattedLastUpdated() {
      if (!this.seoPage?.updated_at) return '';
      const d = new Date(this.seoPage.updated_at);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleDateString();
    },
    relatedPages() {
      const all = Array.isArray(this.seoPages) ? this.seoPages : [];
      const currentSlug = this.$route.params.slug;
      return all.filter((p) => p.slug !== currentSlug).slice(0, 6);
    },
  },
  methods: {
    ...mapActions(['fetchSeoPage', 'loadSeoPageResults', 'fetchSeoPagesList']),
    ...mapMutations(['setErrorMessage']),

    setHead(page) {
      document.title = page?.meta_title || page?.h1 || 'Best Games';

      const description = page?.meta_description || page?.intro || '';

      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', description);

      let robots = document.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'index,follow');

      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', `${window.location.origin}${this.$route.fullPath}`);
    },

    signalPrerenderReady() {
      console.log('[prerender] ready:', this.$route.fullPath);
      document.dispatchEvent(new Event('seo-page-rendered'));
    },

    async loadPage(slug) {
      this.isPageReady = false;
      this.hasFatalError = false;
      this.setErrorMessage(null);

      try {
        const page = await this.fetchSeoPage(slug);
        this.setHead(page);

        await this.loadSeoPageResults(slug);

        try {
          await this.fetchSeoPagesList();
        } catch (error) {
          console.warn('Failed to load related SEO pages:', error);
        }

        this.isPageReady = true;
      } catch (error) {
        console.error('Failed to load SEO page:', error);
        this.hasFatalError = true;
      } finally {
        this.$nextTick(() => {
          this.signalPrerenderReady();
        });
      }
    },
  },

  async mounted() {
    await this.loadPage(this.$route.params.slug);
  },

  async beforeRouteUpdate(to, from, next) {
    await this.loadPage(to.params.slug);
    next();
  },
};
</script>

<style scoped>
.results-warning {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 12px 14px;
  margin-bottom: 20px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #d4d4d8;
  font-size: 0.95rem;
  line-height: 1.45;
}

.results-warning strong {
  color: #f4f4f5;
}

.results-warning.warning {
  border-color: rgba(245, 158, 11, 0.28);
  background: rgba(120, 53, 15, 0.16);
}

.results-warning.info {
  border-color: rgba(59, 130, 246, 0.24);
  background: rgba(30, 64, 175, 0.12);
}

.results-header {
  margin-bottom: 24px;
}

.results-header h2 {
  margin: 0 0 8px;
}

.results-subtitle {
  margin: 0;
  color: #a1a1aa;
}

.empty-state {
  text-align: center;
  padding: 24px 0 8px;
}

.loading-inline {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  padding: 28px 0;
}

.loading-inline p {
  color: #00dc82;
  margin-top: 16px;
  font-weight: 600;
}

.loading-spinner {
  width: 42px;
  height: 42px;
  border: 4px solid #27272a;
  border-top: 4px solid #00dc82;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.error-box {
  text-align: center;
  padding: 12px 0;
}

.browse-link {
  display: inline-block;
  margin-top: 12px;
  color: #00dc82;
  font-weight: 700;
  text-decoration: none;
}

.related-pages {
  margin-top: 40px;
  padding-top: 28px;
  border-top: 1px solid #27272a;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 14px;
}

.related-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  border: 1px solid #27272a;
  border-radius: 8px;
  background: #0d0d0f;
  text-decoration: none;
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.related-card:hover {
  border-color: #00dc82;
  transform: translateY(-1px);
}

.related-card__eyebrow {
  color: #00dc82;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.related-card__title {
  color: #f4f4f5;
}

.related-card__text {
  color: #a1a1aa;
  font-size: 0.92rem;
  line-height: 1.5;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .related-grid {
    grid-template-columns: 1fr;
  }
}
</style>